import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { useAppleCalendar } from "@/hooks/useAppleCalendar";

const AppleCalendarConnect = () => {
  const { connected, checking, calendars, connect, disconnect } = useAppleCalendar();
  const [expanded, setExpanded] = useState(false);
  const [feedUrl, setFeedUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successName, setSuccessName] = useState<string | null>(null);

  // When the calendar is disconnected (from this surface or the other one —
  // both share the same context), drop any lingering "🎉 … is connected!"
  // banner so a removed calendar can't resurface as connected in the connect
  // guide (issue #119).
  useEffect(() => {
    if (!connected) setSuccessName(null);
  }, [connected]);

  const handleConnect = async () => {
    if (!feedUrl.trim()) return;
    setLoading(true);
    setErrorMsg(null);
    setSuccessName(null);
    const result = await connect(feedUrl.trim());
    setLoading(false);
    if (result.success) {
      setSuccessName(result.calendar_name || "Your calendar");
      setFeedUrl("");
      setExpanded(false);
    } else if (result.error === "invalid_link") {
      setErrorMsg(
        "Hmm, that link didn't work. Make sure you copied it from iPhone Calendar → Calendars → ⓘ → Public Calendar → Share Calendar → Copy Link. The link should start with webcal://"
      );
    } else {
      setErrorMsg("Something went wrong. Try again in a moment.");
    }
  };

  if (checking) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-base flex-shrink-0">🍎</span>
        <p className="font-body text-xs" style={{ color: "#54525C" }}>Checking Apple Calendar…</p>
      </div>
    );
  }

  // Connected state, collapsed
  if (connected && !expanded) {
    return (
      <div>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-base flex-shrink-0">🍎</span>
            <div className="min-w-0">
              <p className="font-body text-sm font-semibold" style={{ color: "#1C1A3E" }}>Apple Calendar</p>
              <p className="font-body text-xs" style={{ color: "#16a34a" }}>
                Connected ✓ ({calendars.length} {calendars.length === 1 ? "calendar" : "calendars"})
              </p>
            </div>
          </div>
        </div>
        <div style={{ marginTop: 10 }}>
          {calendars.map((cal) => (
            <div key={cal.id} className="flex items-center justify-between gap-2" style={{ paddingBottom: 6 }}>
              <p className="font-body text-xs truncate" style={{ color: "#1C1A3E" }}>{cal.calendar_name}</p>
              <button
                type="button"
                onClick={() => disconnect(cal.id)}
                className="flex-shrink-0 p-1"
                aria-label={`Remove ${cal.calendar_name}`}
              >
                <X size={14} style={{ color: "#54525C" }} />
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => { setExpanded(true); setErrorMsg(null); setSuccessName(null); }}
            className="font-body text-xs"
            style={{ color: "#5050C8", marginTop: 2 }}
          >
            + Add another calendar
          </button>
        </div>
      </div>
    );
  }

  // Not connected, collapsed
  if (!connected && !expanded) {
    return (
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-base flex-shrink-0">🍎</span>
          <div className="min-w-0">
            <p className="font-body text-sm font-semibold" style={{ color: "#1C1A3E" }}>Apple Calendar</p>
            <p className="font-body text-xs" style={{ color: "#54525C" }}>Not connected</p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="px-4 py-2 rounded-xl font-body font-semibold text-sm text-white flex-shrink-0"
          style={{ backgroundColor: "#5050C8" }}
        >
          Connect
        </button>
      </div>
    );
  }

  // Expanded guide (connect or add another)
  return (
    <div>
      <div className="flex items-center gap-2" style={{ marginBottom: 10 }}>
        <span className="text-base flex-shrink-0">🍎</span>
        <p className="font-body text-sm font-semibold" style={{ color: "#1C1A3E" }}>
          {connected ? "Add another Apple Calendar" : "Connect Apple Calendar"}
        </p>
      </div>

      {successName && (
        <p className="font-body text-sm" style={{ color: "#16a34a", lineHeight: 1.5, marginBottom: 10 }}>
          🎉 {successName} is connected! Your events will appear on your schedule.
        </p>
      )}

      <p className="font-body text-xs" style={{ color: "#54525C", lineHeight: 1.6, marginBottom: 10 }}>
        Connect your iPhone Calendar by sharing a link from the Calendar app — takes about 30 seconds.
      </p>

      <ol className="font-body" style={{ fontSize: 12, color: "#54525C", lineHeight: 1.7, paddingLeft: 18, listStyle: "decimal", marginBottom: 10 }}>
        <li>Open the iPhone <strong style={{ color: "#1C1A3E" }}>Calendar</strong> app</li>
        <li>Tap <strong style={{ color: "#1C1A3E" }}>Calendars</strong> at the bottom of the screen</li>
        <li>Tap the <strong style={{ color: "#1C1A3E" }}>ⓘ</strong> next to the calendar you want to share</li>
        <li>Tap <strong style={{ color: "#1C1A3E" }}>Public Calendar</strong> to turn it on</li>
        <li>Tap <strong style={{ color: "#1C1A3E" }}>Share Calendar</strong>, then <strong style={{ color: "#1C1A3E" }}>Share Link</strong></li>
        <li>Tap <strong style={{ color: "#1C1A3E" }}>Copy Link</strong> — you'll get a webcal:// address — paste it below</li>
      </ol>

      <p className="font-body text-xs" style={{ color: "#54525C", lineHeight: 1.6, marginBottom: 10 }}>
        Public just means "anyone with this exact link can see event titles and times" — Apple doesn't list it anywhere, and you can flip it back off anytime. 🔒
      </p>

      <input
        type="url"
        value={feedUrl}
        onChange={(e) => setFeedUrl(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") handleConnect(); }}
        placeholder="webcal://..."
        className="w-full font-body text-sm"
        style={{
          border: "1.5px solid #C8C8F4",
          borderRadius: 10,
          padding: "8px 12px",
          color: "#1C1A3E",
          outline: "none",
          marginBottom: 8,
          backgroundColor: "white",
        }}
      />

      {errorMsg && (
        <p className="font-body text-xs" style={{ color: "#dc2626", lineHeight: 1.5, marginBottom: 8 }}>
          {errorMsg}
        </p>
      )}

      <button
        type="button"
        onClick={handleConnect}
        disabled={loading || !feedUrl.trim()}
        className="w-full font-body font-semibold text-sm text-white"
        style={{
          height: 40,
          borderRadius: 10,
          backgroundColor: loading || !feedUrl.trim() ? "#A8A8E8" : "#5050C8",
          cursor: loading || !feedUrl.trim() ? "not-allowed" : "pointer",
          marginBottom: 10,
        }}
      >
        {loading ? "Connecting…" : "Connect Calendar"}
      </button>

      <p className="font-body" style={{ fontSize: 11, color: "#7E7B8A", lineHeight: 1.5, fontStyle: "italic", marginBottom: 4 }}>
        🔒 Your events stay in iCloud — Kin only reads titles and times to show them on your schedule.
      </p>
      <p className="font-body" style={{ fontSize: 11, color: "#7E7B8A", lineHeight: 1.5, fontStyle: "italic", marginBottom: 8 }}>
        💡 Already using Google Calendar? This is the Apple version of the same sync — Kin reads both.
      </p>

      <button
        type="button"
        onClick={() => { setExpanded(false); setErrorMsg(null); setFeedUrl(""); }}
        className="font-body text-xs"
        style={{ color: "#54525C", textDecoration: "underline" }}
      >
        Cancel
      </button>
    </div>
  );
};

export default AppleCalendarConnect;
